<ng-container *ngIf="collapsible; else nonCollapsibleTemplate">
    <p-accordion [(activeIndex)]="accordionActiveIndex">
        <p-accordionTab>
            <ng-template pTemplate="header">
                <div class="flex align-items-center justify-content-between w-full pr-2">
                    <div class="flex align-items-center gap-3">
                        <i class="pi pi-file-text text-primary text-xl"></i>
                        <div class="flex flex-column">
                            <span class="font-semibold text-lg" [ngClass]="{'mb-2': getLatestFormAssignment()}">
                                <ng-container *ngIf="getLatestFormAssignment(); else noFormsTemplate">
                                    Latest Form Assignment
                                </ng-container>
                                <ng-template #noFormsTemplate>
                                    <span class="text-500">No Patient Forms Found</span>
                                </ng-template>
                            </span>
                            <div class="flex align-items-center gap-4 text-sm text-600"
                                *ngIf="getLatestFormAssignment() as latestAssignment">
                                <span class="flex align-items-center gap-1">
                                    <i class="pi pi-calendar"></i>
                                    {{ latestAssignment.assignedAt | dynamicDate:'shortDate' }}
                                </span>
                                <span class="flex align-items-center gap-1" *ngIf="latestAssignment.form?.name">
                                    <i class="pi pi-file"></i>
                                    {{ getFormNameTruncated(latestAssignment) }}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div class="flex align-items-center gap-3" *ngIf="getLatestFormAssignment() as latestAssignment">
                        <div class="flex align-items-center gap-2">
                            <i class="pi text-lg"
                                [ngClass]="[getFormAssignmentIcon(latestAssignment), getFormAssignmentIconColor(latestAssignment)]"></i>
                            <p-tag [value]="getStatusDisplayValue(latestAssignment.status)"
                                [severity]="getStatusSeverity(latestAssignment.status)"
                                [style]="{ 'min-width': '80px', 'text-align': 'center' }">
                            </p-tag>
                        </div>
                        <span class="text-sm text-600" *ngIf="formAssignmentPage.total > 1">
                            +{{ formAssignmentPage.total - 1 }} more
                        </span>
                    </div>
                </div>
            </ng-template>

            <ng-container *ngTemplateOutlet="formsContentTemplate"></ng-container>

        </p-accordionTab>
    </p-accordion>
</ng-container>

<ng-template #nonCollapsibleTemplate>
    <ng-container *ngTemplateOutlet="formsContentTemplate"></ng-container>
</ng-template>

<ng-template #formsContentTemplate>
    <div class="flex justify-content-between align-items-center mb-4">
        <div class="flex align-items-center gap-3">
            <input type="text" pInputText placeholder="Search form assignments..." [formControl]="searchControl"
                class="w-20rem" />
            <p-dropdown [options]="statusFilterOptions" [formControl]="statusFilterControl" placeholder="All Status"
                optionLabel="label" optionValue="value" [showClear]="false" scrollHeight="300px">
            </p-dropdown>
        </div>
    </div>

    <!-- Sorting Controls -->
    <div class="flex flex-wrap gap-2 mb-4 p-3 border-round surface-border border-1">
        <div class="flex align-items-center gap-2">
            <span class="text-sm font-medium">Sort by:</span>
        </div>
        <button pButton class="p-button-outlined p-button-sm"
            [class.p-button-secondary]="pageOptions.orderBy === 'assignedAt'" (click)="onCardSort('assignedAt')">
            Assignment Date
            <i class="pi ml-1" [ngClass]="pageOptions.orderBy === 'assignedAt' ? 
                         (pageOptions.orderDir === OrderDir.ASC ? 'pi-sort-up' : 'pi-sort-down') : 
                         'pi-sort'"></i>
        </button>
        <button pButton class="p-button-outlined p-button-sm"
            [class.p-button-secondary]="pageOptions.orderBy === 'completedAt'" (click)="onCardSort('completedAt')">
            Response Date
            <i class="pi ml-1" [ngClass]="pageOptions.orderBy === 'completedAt' ? 
                         (pageOptions.orderDir === OrderDir.ASC ? 'pi-sort-up' : 'pi-sort-down') : 
                         'pi-sort'"></i>
        </button>
        <button pButton class="p-button-outlined p-button-sm"
            [class.p-button-secondary]="pageOptions.orderBy === 'status'" (click)="onCardSort('status')">
            Status
            <i class="pi ml-1" [ngClass]="pageOptions.orderBy === 'status' ? 
                         (pageOptions.orderDir === OrderDir.ASC ? 'pi-sort-up' : 'pi-sort-down') : 
                         'pi-sort'"></i>
        </button>
        <button pButton class="p-button-outlined p-button-sm"
            [class.p-button-secondary]="pageOptions.orderBy === 'expiresAt'" (click)="onCardSort('expiresAt')">
            Expiry Date
            <i class="pi ml-1" [ngClass]="pageOptions.orderBy === 'expiresAt' ? 
                         (pageOptions.orderDir === OrderDir.ASC ? 'pi-sort-up' : 'pi-sort-down') : 
                         'pi-sort'"></i>
        </button>
        <button pButton class="p-button-outlined p-button-sm"
            [class.p-button-secondary]="pageOptions.orderBy === 'progress'" (click)="onCardSort('progress')">
            Progress
            <i class="pi ml-1" [ngClass]="pageOptions.orderBy === 'progress' ? 
                         (pageOptions.orderDir === OrderDir.ASC ? 'pi-sort-up' : 'pi-sort-down') : 
                         'pi-sort'"></i>
        </button>
    </div>

    <!-- Cards Grid -->
    <div class="flex flex-column gap-4" *ngIf="formAssignmentPage.data && formAssignmentPage.data.length > 0">
        <div class="surface-card border-round-xl shadow-2 p-4 hover:shadow-4 transition-all transition-duration-200 cursor-pointer"
            *ngFor="let assignment of formAssignmentPage.data"
            (click)="onFormCardClick(assignment, assignment.patient?.id)">
            <div class="flex align-items-center gap-4">
                <!-- Assignment Icon -->
                <div class="flex align-items-center justify-content-center w-4rem h-4rem border-round-lg surface-100">
                    <i class="pi text-3xl" [ngClass]="{
                        'pi-file-edit text-green-600': isStatus(assignment, 'COMPLETED'),
                        'pi-file text-blue-600': isStatus(assignment, 'IN_PROGRESS'),
                        'pi-file-o text-orange-600': hasStatusIn(assignment, ['PENDING', 'ASSIGNED']),
                        'pi-times-circle text-red-600': hasStatusIn(assignment, ['EXPIRED', 'CANCELLED'])
                    }"></i>
                </div>

                <!-- Assignment Details -->
                <div class="flex-1 flex flex-column gap-2">
                    <!-- Header with Form Name and Status -->
                    <div class="flex justify-content-between align-items-start">
                        <div class="flex flex-column gap-1 flex-1">
                            <div class="flex align-items-center gap-3">
                                @if (assignment.form?.name) {
                                <h3 class="m-0 text-xl font-semibold text-900" [title]="getFormName(assignment)">
                                    {{ getFormNameTruncated(assignment) }}
                                </h3>
                                } @else {
                                <h3 class="m-0 text-xl font-semibold text-500">No form assigned</h3>
                                }
                                @if (viewMode === 'org' && assignment.patient) {
                                <div class="flex align-items-center gap-2 text-lg font-semibold text-600">
                                    <i class="pi pi-user"></i>
                                    <span class="font-medium">{{ assignment.patient.firstName }} {{
                                        assignment.patient.lastName }}</span>
                                </div>
                                }
                                <!-- Assigned Date -->
                                <div class="flex align-items-center gap-2">
                                    <i class="pi pi-calendar"
                                        [class.text-primary-600]="!isStatus(assignment, 'EXPIRED')"
                                        [class.text-red-600]="isStatus(assignment, 'EXPIRED')"></i>
                                    <span [class.text-primary-600]="!isStatus(assignment, 'EXPIRED')"
                                        [class.text-red-600]="isStatus(assignment, 'EXPIRED')" class="font-light">
                                        {{ assignment.assignedAt | dynamicDate:'shortDate' }}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <p-tag [value]="getStatusDisplayValue(assignment.status)"
                            [severity]="getStatusSeverity(assignment.status)"
                            [style]="{ 'min-width': '80px', 'text-align': 'center' }">
                        </p-tag>
                    </div> <!-- Assignment Meta Info -->
                    <div class="flex align-items-center text-sm text-600 flex-wrap">
                        @if (assignment.form?.version) {
                        <div class="flex align-items-center gap-2 mr-6 mb-1">
                            <i class="pi pi-code"></i>
                            <span class="font-medium">v{{ assignment.form?.version }}</span>
                        </div>
                        }
                        @if (assignment.completedAt) {
                        <div class="flex align-items-center gap-2 mr-6 mb-2">
                            <i class="pi pi-check"></i>
                            <span>Completed: {{ assignment.completedAt | dynamicDate:'shortDate' }}</span>
                        </div>
                        }
                        @if (assignment.expiresAt && !isStatus(assignment, 'COMPLETED')) {
                        <div class="flex align-items-center gap-2 mr-6 mb-2"
                            [class.text-red-600]="isStatus(assignment, 'EXPIRED')">
                            <i class="pi pi-clock"></i>
                            <span>Expires: {{ assignment.expiresAt | dynamicDate:'short' }}</span>
                        </div>
                        }
                        <!-- Actions for completed -->
                        @if (isStatus(assignment, 'COMPLETED') && assignment.responseData) {
                        <div class="flex align-items-center gap-3 mr-6 mb-2">
                            <button pButton type="button" icon="pi pi-download"
                                style="font-size:1.5rem; height:2.75rem; width:2.75rem;"
                                class="p-button-outlined p-button-primary"
                                (click)="onDownloadCsv(assignment); $event.stopPropagation()" pTooltip="Download CSV"
                                tooltipPosition="top"></button>
                            <button pButton type="button" icon="pi pi-eye"
                                style="font-size:1.5rem; height:2.75rem; width:2.75rem;"
                                class="p-button-outlined p-button-info"
                                (click)="onViewResponse(assignment); $event.stopPropagation()" pTooltip="View Response"
                                tooltipPosition="top"></button>
                        </div>
                        }
                    </div>

                    <!-- Progress Bar -->
                    @if (assignment.progress !== null && assignment.progress !== undefined && !isStatus(assignment,
                    'COMPLETED')) {
                    <div class="flex align-items-center gap-2 mt-2">
                        <span class="text-sm text-600 font-medium">Progress:</span>
                        <div class="flex-1 bg-gray-200 border-round" style="height: 0.5rem; max-width: 200px;">
                            <div class="bg-primary border-round h-full transition-all transition-duration-300"
                                [style.width.%]="assignment.progress || 0">
                            </div>
                        </div>
                        <span class="text-sm font-medium">{{ assignment.progress || 0 }}%</span>
                    </div>
                    }
                </div>
            </div>
        </div>
    </div>

    <!-- Pagination -->
    <div class="mt-4" *ngIf="formAssignmentPage.data && formAssignmentPage.data.length > 0">
        <p-paginator [first]="(pageOptions.page - 1) * pageOptions.limit" [rows]="pageOptions.limit"
            [totalRecords]="formAssignmentPage.total" [rowsPerPageOptions]="[10, 25, 50, 100]"
            (onPageChange)="onPaginatorPageEvent($event)" [showCurrentPageReport]="true"
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} form assignments">
        </p-paginator>
    </div>

    <!-- Empty State -->
    <div class="text-center py-8" *ngIf="!formAssignmentPage.data || formAssignmentPage.data.length === 0">
        <div class="flex flex-column align-items-center gap-3">
            <div class="flex align-items-center justify-content-center w-6rem h-6rem border-round-xl surface-100">
                <i class="pi pi-file text-4xl text-400"></i>
            </div>
            <div class="flex flex-column gap-2">
                <span class="text-lg text-color-secondary">No form assignments found</span>
                <p class="m-0 text-color-secondary line-height-3">
                    @if (searchControl.value) {
                    Try adjusting your search criteria
                    } @else {
                    @if (viewMode === "patient") {
                    No form assignments have been created for this patient yet
                    }
                    @if (viewMode === "org") {
                    No form assignments have been created in this organisation yet
                    }
                    }
                </p>
            </div>
        </div>
    </div>
</ng-template>

<!-- Add Form Response Viewer Dialog -->
<app-form-response-viewer *ngIf="showResponseViewer" [visible]="showResponseViewer" [data]="responseViewerData"
    [showDownloadButton]="true" [showCloseButton]="true" [zIndex]="1000" (visibleChange)="onCloseResponseViewer()"
    (close)="onCloseResponseViewer()" appendTo="body">
</app-form-response-viewer>